import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Button } from '@mui/material';
import { Plus } from 'lucide-react';

import { type TypeDataset } from '../../common/dataTypes';
import { useGetProjectById } from '../../hooks';
import { useGetDatasets } from '../../hooks/tanstack/useDatasetsHook';
import {
  useGetRoles,
  useGetRouteParams,
  useGetSearchResults,
} from '../../hooks/tanstack/useUtilsHook';
import CreateDatasetDialog from '../dataset/CreateDatasetDialog';
import DatasetsTable from '../dataset/DatasetsTable';
import SearchInput from '../SearchInput';

const ProjectDatasetsManagement = () => {
  const { projectId, orgId } = useGetRouteParams();

  const [openCreateDatasetDialog, setOpenCreateDatasetDialog] = useState(false);

  const { projectRole } = useGetRoles();

  const form = useForm();

  const { watch } = form;

  const handleCloseCreateDatasetDialog = () => {
    setOpenCreateDatasetDialog(false);
  };

  const {
    data: datasets,
    isLoading: isFetchingDatasets,
    isRefetching: isRefetchingDatasets,
  } = useGetDatasets(
    {
      projectId,
      orgId,
    },
    {
      enabled: !!projectId && !!orgId,
    }
  );

  const { data: project } = useGetProjectById(
    {
      projectId,
      orgId,
    },
    {
      enabled: !!projectId && !!orgId,
    }
  );

  const searchResults = useGetSearchResults<TypeDataset>({
    searchValue: watch('search'),
    data: datasets,
    filterFields: ['name', 'description', 'created_by.full_name'],
  });

  const isAdmin = projectRole === 'project_owner' && !project?.is_guest_project;

  const isLoading = isFetchingDatasets || isRefetchingDatasets;

  return (
    <div className="flex w-full flex-col gap-6 px-12 pb-6">
      <FormProvider {...form}>
        <h1 className="text-2xl font-bold">Dataset</h1>
        <div className="flex items-center justify-between">
          <form className="flex items-center gap-3">
            <div className="w-[300px]">
              <SearchInput />
            </div>
          </form>
          {isAdmin && (
            <Button
              onClick={() => {
                setOpenCreateDatasetDialog(true);
              }}
              variant="contained"
              style={{
                color: 'white',
                backgroundColor: '#B8341B',
              }}
            >
              <div className="flex items-center gap-2">
                <Plus width={20} height={20} color="#E8F2FF" />
                New
              </div>
            </Button>
          )}
          <CreateDatasetDialog
            open={openCreateDatasetDialog}
            handleCloseDialog={handleCloseCreateDatasetDialog}
            handleSubmitDialog={handleCloseCreateDatasetDialog}
          />
        </div>
        <DatasetsTable defaultDatasets={searchResults} isLoading={isLoading} />
      </FormProvider>
    </div>
  );
};

export default ProjectDatasetsManagement;
